import { pickLang, pickLangStrict } from './i18nDisplay';

export const LANGS = ['en', 'ru', 'sr'];

/** Coerce card body to i18n map (mobile may send plain string or alternate keys). */
export function normalizeCardBodyHtml(card) {
  if (!card || typeof card !== 'object') return {};
  const raw = card.bodyHtml;
  if (typeof raw === 'string' && raw.trim()) {
    const s = raw.trim();
    return { en: s, ru: s, sr: s };
  }
  if (raw && typeof raw === 'object') {
    return { ...raw };
  }
  const alt =
    card.body ??
    card.text ??
    card.contentHtml ??
    card.content ??
    card.html;
  if (typeof alt === 'string' && alt.trim()) {
    const s = alt.trim();
    return { en: s, ru: s, sr: s };
  }
  if (alt && typeof alt === 'object') {
    return { ...alt };
  }
  return {};
}

/** Resolved HTML for one slide in UI language (with fallbacks). */
export function pickCardBodyHtml(card, lang) {
  const map = normalizeCardBodyHtml(card);
  if (Object.keys(map).length === 0) return '';
  return pickLang(map, lang);
}

/** Card body for one locale only (no fallback). */
export function pickCardBodyHtmlStrict(card, lang) {
  const map = normalizeCardBodyHtml(card);
  const v = map[lang];
  return typeof v === 'string' ? v.trim() : '';
}

const PSEUDO_BODY_TAGS = [
  [/<\s*\/\s*bold\s*>/gi, '</strong>'],
  [/<\s*bold\s*>/gi, '<strong>'],
  [/<\s*\/\s*underline\s*>/gi, '</u>'],
  [/<\s*underline\s*>/gi, '<u>'],
  [/<\s*\/\s*italic\s*>/gi, '</em>'],
  [/<\s*italic\s*>/gi, '<em>'],
];

/** Prepare card body HTML for web display (line breaks, pseudo-tags, basic safety). */
export function formatArticleBodyHtml(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let html = raw.replace(/\r\n?/g, '\n').trim();
  if (!html) return '';

  PSEUDO_BODY_TAGS.forEach(([pattern, replacement]) => {
    html = html.replace(pattern, replacement);
  });

  if (!/<(p|div|ul|ol|h[1-6])\b/i.test(html)) {
    html = html.replace(/\n/g, '<br>');
  }

  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

/**
 * Normalize and sort Firestore cards; ensure stable ids.
 */
export function normalizeCardsArray(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const filtered = raw.filter((c) => c && typeof c === 'object');
  const copy = [...filtered];
  const allHaveOrder = copy.every((c) => typeof c.order === 'number');
  if (allHaveOrder) copy.sort((a, b) => (a.order || 0) - (b.order || 0));

  return copy.map((c, i) => {
    const id = typeof c.id === 'string' && c.id ? c.id : `card-${i}`;
    const title =
      c.title && typeof c.title === 'object' ? { ...c.title } : {};
    const bodyHtml = normalizeCardBodyHtml(c);
    return {
      ...c,
      id,
      title,
      bodyHtml,
      imageUrl: typeof c.imageUrl === 'string' ? c.imageUrl : '',
    };
  });
}

/** Card contributes content if body in any lang or image set. */
export function isArticleCardNonempty(card) {
  if (!card) return false;
  const img = !!(card.imageUrl && String(card.imageUrl).trim());
  const map = normalizeCardBodyHtml(card);
  const txt = LANGS.some(
    (l) => !!(map[l] && String(map[l]).trim()),
  );
  return img || txt;
}

/**
 * Legacy HTML mirrors card bodies joined per language (for mobile/web fallback).
 */
export function deriveLegacyHtmlFromCards(cards) {
  const out = { en: '', ru: '', sr: '' };
  if (!cards?.length) return out;

  LANGS.forEach((lang) => {
    const parts = cards
      .map((card) => {
        const m = normalizeCardBodyHtml(card);
        return (m[lang] || '').trim();
      })
      .filter(Boolean);
    out[lang] = parts.join('<hr/>');
  });
  return out;
}

function slideHasContent(slide) {
  if (slide.isIntro) {
    return !!(
      (slide.cardTitle && String(slide.cardTitle).trim()) ||
      (slide.subtitle && String(slide.subtitle).trim()) ||
      (slide.imageUrl && String(slide.imageUrl).trim())
    );
  }
  return !!(
    (slide.cardTitle && String(slide.cardTitle).trim()) ||
    (slide.bodyHtml && String(slide.bodyHtml).trim()) ||
    (slide.imageUrl && String(slide.imageUrl).trim())
  );
}

/**
 * Slide 1: cover + article title + subtitle only.
 * Slides 2+: every Firestore card (including the first) with full card content.
 */
export function buildArticleSlides(article, lang) {
  if (!article) return [];

  const cards = normalizeCardsArray(article.cards);
  const nonempty = cards.filter(isArticleCardNonempty);

  if (nonempty.length > 0) {
    const cover =
      article.coverUrl && String(article.coverUrl).trim()
        ? String(article.coverUrl).trim()
        : '';

    const slides = [];

    const introSlide = {
      key: 'intro',
      isIntro: true,
      cardTitle: pickLangStrict(article.title, lang),
      subtitle: pickLangStrict(article.subtitle, lang),
      bodyHtml: '',
      imageUrl: cover,
    };
    if (slideHasContent(introSlide)) {
      slides.push(introSlide);
    }

    nonempty.forEach((card) => {
      const contentSlide = {
        key: card.id,
        isIntro: false,
        cardTitle: pickLangStrict(card.title, lang),
        subtitle: '',
        bodyHtml: pickCardBodyHtmlStrict(card, lang),
        imageUrl: card.imageUrl || '',
      };
      if (slideHasContent(contentSlide)) {
        slides.push(contentSlide);
      }
    });

    return slides;
  }

  const cover =
    article.coverUrl && String(article.coverUrl).trim()
      ? String(article.coverUrl).trim()
      : '';

  const introOnly = {
    key: 'intro',
    isIntro: true,
    cardTitle: pickLangStrict(article.title, lang),
    subtitle: pickLangStrict(article.subtitle, lang),
    bodyHtml: '',
    imageUrl: cover,
  };

  return slideHasContent(introOnly) ? [introOnly] : [];
}

/** List row title for current locale (article title or first card title). */
export function articleListTitle(article, lang) {
  const t = pickLangStrict(article?.title, lang);
  if (t) return t;
  const cards = normalizeCardsArray(article?.cards).filter(isArticleCardNonempty);
  if (cards[0]) return pickLangStrict(cards[0].title, lang);
  return '';
}

/** Article has content for this locale (no fallback to other languages). */
export function articleHasLocale(article, lang) {
  if (!article || !lang) return false;

  if (pickLangStrict(article.title, lang)) return true;

  const cards = normalizeCardsArray(article.cards).filter(isArticleCardNonempty);
  if (cards.length > 0) {
    return cards.some((card) => {
      const t = pickLangStrict(card.title, lang);
      const b = pickCardBodyHtmlStrict(card, lang);
      return !!(t || b);
    });
  }

  return false;
}

/** Comma-separated category labels in dictionary order (spec §3.2). */
export function categoriesLineFromArticle(
  categoryIds,
  sortedCategories,
  lang,
) {
  if (
    !Array.isArray(categoryIds) ||
    categoryIds.length === 0 ||
    !sortedCategories?.length
  ) {
    return '';
  }
  const idSet = new Set(categoryIds);
  const labels = [];
  sortedCategories.forEach((c) => {
    if (!idSet.has(c.id)) return;
    const name =
      typeof c.name === 'object' ? pickLang(c.name, lang) : '';
    if (name) labels.push(name);
  });
  return labels.join(', ');
}

/** Same for pet tags. */
export function petTagsLineFromArticle(petTagIds, sortedTags, lang) {
  if (!Array.isArray(petTagIds) || petTagIds.length === 0 || !sortedTags?.length)
    return '';
  const idSet = new Set(petTagIds);
  const labels = [];
  sortedTags.forEach((t) => {
    if (!idSet.has(t.id)) return;
    const name = typeof t.name === 'object' ? pickLang(t.name, lang) : '';
    if (name) labels.push(name);
  });
  return labels.join(', ');
}

export function articleMatchesCategoryChip(article, categoryId, allToken) {
  if (!categoryId || categoryId === allToken) return true;
  const ids = article.categoryIds;
  return Array.isArray(ids) && ids.includes(categoryId);
}

/** Visible in web explore (excludes mobile-only). */
export function articleVisibleOnWeb(article) {
  return !article?.mobileOnly;
}

/** Visible in mobile explore (excludes web-only). */
export function articleVisibleOnMobile(article) {
  return !article?.webOnly;
}

/** Pinned first, then by publishedAt desc. */
export function sortArticlesForDisplay(articles) {
  return [...articles].sort((a, b) => {
    const pinDiff = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    if (pinDiff !== 0) return pinDiff;
    return (b.publishedAt || 0) - (a.publishedAt || 0);
  });
}

export function filterArticlesForWeb(articles, lang) {
  return sortArticlesForDisplay(
    (articles || []).filter(
      (a) => articleVisibleOnWeb(a) && articleHasLocale(a, lang),
    ),
  );
}
